import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Lote 152 — gaveta lateral "Assistente IA | Equipe". Quem abre (botões do topo, atalhos) só chama abrir("ia" | "equipe").
// Lote 153 — o Assistente pode ter um CONTEXTO: "Minha agenda" (padrão) ou "Este processo" (resumo das movimentações).
// Lote 154 — o contexto agora vem SOZINHO da tela aberta: a página do processo / da audiência se registra com
// `useContextoAssistente({ tipo, id, rotulo })` e sai do contexto ao fechar. A pessoa pode tirar o contexto (X) naquela tela.
// `pedido` é uma pergunta pronta que a gaveta faz sozinha ao abrir (ex.: botão "Resumir movimentações" no processo).
const GavetaContext = createContext({
  aberta: false, aba: "ia", contexto: null, pedido: null,
  abrir: () => {}, fechar: () => {}, alternar: () => {}, setAba: () => {}, resumirProcesso: () => {}, limparContexto: () => {},
  registrarContexto: () => {}, consumirPedido: () => {},
});
export const useGaveta = () => useContext(GavetaContext);

export const chaveContexto = (c) => (c ? `${c.tipo}:${c.id}` : "agenda");

export function GavetaProvider({ children }) {
  const [estado, setEstado] = useState({ aberta: false, aba: "ia", pedido: null });
  const [daPagina, setDaPagina] = useState(null);       // contexto declarado pela tela aberta
  const [ignorado, setIgnorado] = useState(null);       // chave do contexto que a pessoa dispensou (vale só naquela tela)
  const abrir = useCallback((aba = "ia") => setEstado((s) => ({ ...s, aberta: true, aba })), []);
  const fechar = useCallback(() => setEstado((s) => ({ ...s, aberta: false })), []);
  const alternar = useCallback((aba) => setEstado((s) => (s.aberta && s.aba === aba ? { ...s, aberta: false } : { ...s, aberta: true, aba })), []);
  const setAba = useCallback((aba) => setEstado((s) => ({ ...s, aba })), []);
  const consumirPedido = useCallback(() => setEstado((s) => (s.pedido ? { ...s, pedido: null } : s)), []);
  const registrarContexto = useCallback((c) => setDaPagina(c), []);
  const resumirProcesso = useCallback((processoId, rotulo) => {
    setDaPagina((atual) => (atual && atual.tipo === "processo" && atual.id === processoId ? atual : { tipo: "processo", id: processoId, rotulo }));
    setIgnorado(null);
    setEstado((s) => ({ ...s, aberta: true, aba: "ia", pedido: { n: (s.pedido?.n || 0) + 1, texto: "" } }));
  }, []);
  const limparContexto = useCallback(() => {
    setIgnorado(chaveContexto(daPagina));
    setEstado((s) => ({ ...s, pedido: null }));
  }, [daPagina]);
  // trocou de tela/registro: a dispensa anterior não vale mais
  useEffect(() => { setIgnorado(null); }, [daPagina?.tipo, daPagina?.id]);
  const contexto = daPagina && ignorado !== chaveContexto(daPagina) ? daPagina : null;
  const valor = useMemo(() => ({ ...estado, contexto, abrir, fechar, alternar, setAba, resumirProcesso, limparContexto, registrarContexto, consumirPedido }),
    [estado, contexto, abrir, fechar, alternar, setAba, resumirProcesso, limparContexto, registrarContexto, consumirPedido]);
  return <GavetaContext.Provider value={valor}>{children}</GavetaContext.Provider>;
}

// Chamado pela página que tem um registro aberto. `ctx` null = nada a declarar (ainda carregando).
export function useContextoAssistente(ctx) {
  const { registrarContexto } = useGaveta();
  const tipo = ctx?.tipo, id = ctx?.id, rotulo = ctx?.rotulo;
  useEffect(() => {
    registrarContexto(tipo && id ? { tipo, id, rotulo } : null);
    return () => registrarContexto(null);
  }, [registrarContexto, tipo, id, rotulo]);
}
